<script setup lang="ts">
import type { UniquePost } from "@/server/api/types";
import { setSEO } from "@/utils/seoUtils";
import { ROUTE_BLOG_CATEGORY } from "~/constants/routes";
import { formatDate } from "@/utils/helpers";

const route = useRoute();

definePageMeta({
  layout: "blog-page-slug",
  scrollToTop: true,
});
const config = useRuntimeConfig();
const { replaceParams } = useRouteParams();
const slug = computed(() => {
  return route.params.slug as string;;
});

const { data } = await useLazyAsyncData(
  `blog-post-${slug.value}`,
  async () => {
    const [categoriesResponse, postResponse] = await Promise.all([
      $fetch("/api/content/categories"),
      $fetch(`/api/content/posts/${route.params.slug}`),
    ]);

    return {
      categories: Object.values(categoriesResponse) ?? [],
      post: postResponse,
    };
  },
  { watch: [slug] }
);

const categories = computed(() => data?.value?.categories ?? []);

const post = computed(() => data?.value?.post ?? null);

setSEO({
  seo: post.value?.seo,
  modifiedAt: post.value?.modified,
  publishedAt: post.value?.date,
});

const relatedPosts = computed(() => {
  if (!post.value) {
    return [];
  }
  const currentPostSlug = route.params.slug;
  const postsByTag = post.value?.tags?.edges?.flatMap(
    (tag) => tag.node.posts.edges
  );
  const uniquePosts: UniquePost[] = [];

  postsByTag.forEach((postEdge) => {
    const post = postEdge.node;
    if (
      !uniquePosts.find((uniquePost) => uniquePost.id === post.id) &&
      post.slug !== currentPostSlug
    ) {
      uniquePosts.push({
        id: post?.id,
        slug: post?.slug,
        title: post?.title,
        link: `/blog/${post?.slug}/`,
        categories: post?.categories?.nodes,
        excerpt: post?.excerpt,
        featuredImage: post?.featuredImage?.node?.sourceUrl,
      });
    }
  });

  return uniquePosts;
});

const backgroundHeaderDesktopUrl = computed(() => `url(${post.value?.featuredImage?.node?.sourceUrl})`);

const shareLinkUrl = `${config.public.baseUrl}/blog/${slug.value}`;
</script>

<template>
  <section class="mt-12">
    <BlogPageCategoryNavbar :categories="categories">
      <template #search>
        <BlogSearch />
      </template>
    </BlogPageCategoryNavbar>
    <div
      class="flex flex-col xl:flex-row items-center xl:items-start justify-center gap-6 md:mb-12 max-md:w-[92%] max-md:mx-auto">
      <article
        class="w-[97%] max-xl:-mt-2 xl:w-[62%] bg-primary-cream-100 rounded-[12px] py-6 overflow-x-scroll">
        <header class="mb-[15px] px-6">
          <div class="flex flex-col items-center justify-center">
            <div
              v-if="post?.featuredImage?.node?.sourceUrl"
              class="mb-6 w-full h-[180px] md:h-[260px] bg-center bg-no-repeat bg-cover rounded-[8px]"
              :style="{ backgroundImage: backgroundHeaderDesktopUrl }" />


            <h1
              class="font-black text-desktop-header-l text-center justify-center md:w-[70%] mb-6 mt-2">
              {{ post?.title }}
            </h1>
            <time v-if="post?.date" class="text-desktop-body-xs mb-2">{{ formatDate(post?.date) }}
            </time>
            <div class="flex flex-row gap-1 mb-6">
              <template v-for="(category) in post?.categories?.nodes" :key="category?.name">
                <nuxt-link
                  :to="{ path: replaceParams(ROUTE_BLOG_CATEGORY, { slug: category?.slug }) }"
                  rel="category tag">
                  <div
                    class="px-2 py-1 bg-none hover:bg-tertiary-misc-limeade transition-all rounded-xl outline outline-1 outline-offset-[-1px] outline-primary-spinach-500 inline-flex justify-center items-center">
                    <div class="justify-center text-primary-spinach-500 text-desktop-body-xs uppercase">
                      {{ category?.name }}</div>
                  </div>
                </nuxt-link>
              </template>
            </div>
          </div>
        </header>
        <div class="max-md:px-6 md:px-16">
          <div
            class="wysiwyg [&_h1]:text-[1.875rem] [&_h1]:leading-[1.13] [&_h2]:text-[24px] [&_h3]:text-[18px] [&_h4]:text-[16px] [&_h5]:text-[15px] [&_h6]:text-[14px] [&_strong]:font-bold [&_ul:not(.list-none)]:list-disc [&_ul:not(.list-none)]:pl-[30px] [&_ol:not(.list-none)]:list-decimal [&_ol:not(.list-none)]:pl-[30px] [&_a]:underline [&_a:hover]:no-underline [&_a]:text-primary-kale-500 w-fill"
            v-html="post?.content" />
          <footer
            class="overflow-hidden border-b md:border-b-0 border-t border-gray-[#707070]/20 my-[25px] pt-7 pb-0.5 sm:pt-9 sm:mt-[38px] sm:mb-[30px] text-[13px] sm:text-[14px] leading-[1.57]">
            <div class="float-left mr-[23px] w-[74px] sm:w-[128px]">
              <img
                v-if="post?.author?.node?.authorMeta?.avatar?.node?.sourceUrl"
                :src="post?.author?.node?.authorMeta?.avatar?.node?.sourceUrl"
                :alt="post?.author?.node?.authorMeta?.avatar?.node?.altText" class="attachment-avatar rounded-xl">
              <img
                v-else
                src="/assets/images/logo-n.svg"
                alt="Nurture Life"
                class="attachment-avatar w-full">
            </div>
            <div class="sm:overflow-hidden sm:py-4">
              <h3
                v-if="post?.author?.node?.name"
                class="text-[15px] sm:text-[18px] mb-1.5 sm:mb-3 font-bold">
                {{ post?.author?.node?.name }}
              </h3>
              <p v-if="post?.author?.node?.description">
                {{ post?.author?.node?.description }}
              </p>
            </div>
          </footer>
          <div>
            <NuxtLink
              to="/blog/"
              class="text-primary-kale-500 hover:text-primary-spinach-500 inline-flex items-center align-top text-desktop-subheader-m uppercase">
              <i class="icon-main-chevron-a-left text-[13px] mr-[11px]" />
              <span data-back-to-posts-link class="underline">Back to Posts</span>
            </NuxtLink>
          </div>
        </div>
      </article>
      <div class="flex flex-col !content-center gap-4 w-[90%] xl:w-[20%] xl:min-w-[300px] mb-8">
        <div class="!w-full bg-primary-cream-600 rounded-[12px] p-6">
          <div class="text-desktop-header-m text-primary-kale-500 text-center mb-4">Stay updated!</div>
          <SubscribeForm class="!w-full" />
        </div>
        <div class="w-full !flex !justify-center bg-primary-cream-600 rounded-[12px] p-3">
          <ShareButtons :link="shareLinkUrl" :slug="slug" class="!relative !flex !flex-row !w-fit" />
        </div>

      </div>
    </div>
  </section>
  <section
    v-if="relatedPosts.length > 0"
    class="pt-12 sm:pt-[71px] bg-tertiary-misc-limeade -mb-[93px] pb-[129px]">
    <div class="container">
      <h3 class="text-center text-desktop-header-m mb-8">
        Related Posts
      </h3>
      <BlogRelatedPostList :related-posts="relatedPosts" />
    </div>
  </section>
</template>